import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import meetingService from './meetingService'

const initialState = {
    meetings: [],
    calendarMeetings: [],
    meetingsPast: [],
    meetingsFront: [],
    meetingUsers: [],
    meetingAnnouncements: [],
    meeting: null,
    meetingNotes: [],
    meetingAgendas: [],
    meetingDecisions: [],
    meetingAIDetail: [],
    meetingSpeakers: [],
    meetingSpeechAnalysis: {},
    lionNotesBySection: {
        lastWeek: [],
        issues: [],
        opportunities: [],
        nextWeek: [],
    },
    loadingStates: {
        createMeeting: false,
        getMeetings: false,
        getMeetingsPage: false,
        getMeetingsByMonth: false,
        getFutureMeetings: false,
        getPastMeetings: false,
        getMeetingsFront: false,
        getMeetingsPast: false,
        getSubMeetings: false,
        getMeeting: false,
        subscribeMeeting: false,
        deleteMeeting: false,
        updateMeeting: false,
        updateMeetingRecord: false,
        getMeetingAgendas: false,
        getMeetingAnnouncements: false,
        getMeetingNotes: false,
        createMeetingNote: false,
        joinMeeting: false,
        getMeetingUsers: false,
        getMeetingAIDetail: false,
        getMeetingSpeakers: false,
        getMeetingSpeechAnalysis: false,
        getMeetingDecisions: false,
        subscribeLionNotes: false,
        createLionNote: false,
    },
    errors: {
        createMeeting: null,
        getMeetings: null,
        getMeetingsPage: null,
        getMeetingsByMonth: null,
        getFutureMeetings: null,
        getPastMeetings: null,
        getMeetingsFront: null,
        getMeetingsPast: null,
        getSubMeetings: null,
        getMeeting: null,
        subscribeMeeting: null,
        deleteMeeting: null,
        updateMeeting: null,
        updateMeetingRecord: null,
        getMeetingAgendas: null,
        getMeetingAnnouncements: null,
        getMeetingNotes: null,
        createMeetingNote: null,
        joinMeeting: null,
        getMeetingUsers: null,
        getMeetingAIDetail: null,
        getMeetingSpeakers: null,
        getMeetingSpeechAnalysis: null,
        getMeetingDecisions: null,
        subscribeLionNotes: null,
        createLionNote: null,
    },
    isError: false,
    isSuccess: false,
    isLoading: false,
    message: '',
    meetingsPagination: {
        hasMore: true,
        nextCursor: null,
    },
}

// Reducer'da veriyi normalize eden bir yardımcı fonksiyon
const normalizeFirebaseData = (data) => {
    if (!data) return data;

    // Eğer data bir array ise
    if (Array.isArray(data)) {
        return data.map(item => normalizeFirebaseData(item));
    }

    // Eğer data bir object ise
    if (typeof data === 'object') {
        const normalized = {};

        for (const [key, value] of Object.entries(data)) {
            // Timestamp objelerini Unix timestamp'e çevir
            if (value && typeof value.toDate === 'function') {
                normalized[key] = value.toDate().getTime();
            }
            // İç içe objeleri/arrayleri recursive olarak normalize et
            else if (value && typeof value === 'object') {
                normalized[key] = normalizeFirebaseData(value);
            }
            // Diğer değerleri olduğu gibi kopyala
            else {
                normalized[key] = value;
            }
        }

        return normalized;
    }

    return data;
};

// Thunk'lar createSlice'tan önce tanımlanmalı (TDZ fix)
export const createMeeting = createAsyncThunk('meeting/createMeeting', async (meetingData, thunkAPI) => {
    try {
        const userId = thunkAPI.getState().auth.user.uid
        const data = await meetingService.createMeeting(meetingData, userId)
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message;
        return thunkAPI.rejectWithValue(message);
    }
})


export const getMeetings = createAsyncThunk('meeting/getMeetings', async (_, thunkAPI) => {
    try {
        const userId = thunkAPI.getState().auth.user.uid
        const data = await meetingService.getMeetings(userId)
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getMeetingsPage = createAsyncThunk(
    'meeting/getMeetingsPage',
    async ({ pageSize = 15, cursor = null, searchTerm = '', append = false } = {}, thunkAPI) => {
        try {
            const userId = thunkAPI.getState().auth.user.uid
            const data = await meetingService.getMeetingsPage({ userId, pageSize, cursor, searchTerm })
            return { ...normalizeFirebaseData(data), append }
        } catch (error) {
            const message = error.message
            return thunkAPI.rejectWithValue(message)
        }
    }
)

export const getMeetingsByMonth = createAsyncThunk(
    'meeting/getMeetingsByMonth',
    async ({ year, monthIndex }, thunkAPI) => {
        try {
            const userId = thunkAPI.getState().auth.user.uid
            const data = await meetingService.getMeetingsByMonth(userId, year, monthIndex)
            return normalizeFirebaseData(data)
        } catch (error) {
            const message = error.message
            return thunkAPI.rejectWithValue(message)
        }
    }
)

export const getFutureMeetings = createAsyncThunk('meeting/getFutureMeetings', async (_, thunkAPI) => {
    try {
        const userId = thunkAPI.getState().auth.user.uid
        const data = await meetingService.getFutureMeetings(userId)
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getPastMeetings = createAsyncThunk('meeting/getPastMeetings', async (_, thunkAPI) => {
    try {
        const userId = thunkAPI.getState().auth.user.uid
        const data = await meetingService.getPastMeetings(userId)
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getMeetingsFront = createAsyncThunk('meeting/getMeetingsFront', async (_, thunkAPI) => {
    try {
        const data = await meetingService.getMeetingsFront()
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getMeetingsPast = createAsyncThunk('meeting/getMeetingsPast', async (_, thunkAPI) => {
    try {
        const data = await meetingService.getMeetingsPast()
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getSubMeetings = createAsyncThunk('meeting/getSubMeetings', async (meetingId, thunkAPI) => {
    try {
        const data = await meetingService.getSubMeetings(meetingId)
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getMeeting = createAsyncThunk('meeting/getMeeting', async (id, thunkAPI) => {
    try {
        const data = await meetingService.getMeeting(id)
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const subscribeMeeting = createAsyncThunk(
    'meeting/subscribeMeeting',
    async (meetingId, thunkAPI) => {
        try {
            const unsubscribe = meetingService.subscribeMeeting(meetingId, (data) => {
                thunkAPI.dispatch(setMeeting(normalizeFirebaseData(data)))
            })
            return unsubscribe
        } catch (error) {
            const message = error.message
            return thunkAPI.rejectWithValue(message)
        }
    }
)

export const deleteMeeting = createAsyncThunk('meeting/deleteMeeting', async (id, thunkAPI) => {
    try {
        return await meetingService.deleteMeeting(id)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const updateMeeting = createAsyncThunk('meeting/updateMeeting', async ({ meetingData, id }, thunkAPI) => {
    try {
        const data = await meetingService.updateMeeting(meetingData, id)
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const updateMeetingRecord = createAsyncThunk(
    'meetings/updateRecord',
    async ({ meetingData, id, onProgress }, thunkAPI) => {
        try {
            const data = await meetingService.updateMeetingRecord(meetingData, id, onProgress)
            return normalizeFirebaseData(data)
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString()
            return thunkAPI.rejectWithValue(message)
        }
    }
)

export const getMeetingAgendas = createAsyncThunk('meeting/getMeetingAgendas', async (meetingId, thunkAPI) => {
    try {
        const data = await meetingService.getMeetingAgendas(meetingId)
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getMeetingAnnouncements = createAsyncThunk('meeting/getMeetingAnnouncements', async (meetingId, thunkAPI) => {
    try {
        const data = await meetingService.getMeetingAnnouncements(meetingId)
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getMeetingNotes = createAsyncThunk('meeting/getMeetingNotes', async (meetingId, thunkAPI) => {
    try {
        const userId = thunkAPI.getState().auth.user.uid
        const data = await meetingService.getMeetingNotes(meetingId, userId)
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const createMeetingNote = createAsyncThunk('meeting/createMeetingNote', async (noteData, thunkAPI) => {
    try {
        const userId = thunkAPI.getState().auth.user.uid
        const data = await meetingService.createMeetingNote(noteData, noteData.meetingId, userId)
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message;
        return thunkAPI.rejectWithValue(message);
    }
})

export const joinMeeting = createAsyncThunk('meeting/etkinligekatil', async (meetingId, thunkAPI) => {
    try {
        const userId = thunkAPI.getState().auth.user.uid
        const data = await meetingService.joinMeeting(meetingId, userId)
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getMeetingUsers = createAsyncThunk('meeting/getMeetingUsers', async (meetingId, thunkAPI) => {
    try {
        const data = await meetingService.getMeetingUsers(meetingId)
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getMeetingAIDetail = createAsyncThunk('meeting/getMeetingAIDetail', async (meetingId, thunkAPI) => {
    try {
        const data = await meetingService.getMeetingAIDetail(meetingId)
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getMeetingSpeakers = createAsyncThunk('meeting/getMeetingSpeakers', async (meetingId, thunkAPI) => {
    try {
        const data = await meetingService.getMeetingSpeakers(meetingId)
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getMeetingSpeechAnalysis = createAsyncThunk('meeting/getMeetingSpeechAnalysis', async (meetingId, thunkAPI) => {
    try {
        const data = await meetingService.getMeetingSpeechAnalysis(meetingId)
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})


export const getMeetingDecisions = createAsyncThunk('meeting/getMeetingDecisions', async (meetingId, thunkAPI) => {
    try {
        const data = await meetingService.getMeetingDecisions(meetingId)
        return normalizeFirebaseData(data)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const subscribeLionNotes = createAsyncThunk(
    'meeting/subscribeLionNotes',
    async (meetingId, thunkAPI) => {
        try {
            const unsubscribe = meetingService.subscribeLionNotes(meetingId, (buckets) => {
                thunkAPI.dispatch(setLionNotesBySection(buckets))
            })
            return unsubscribe
        } catch (error) {
            const message = error.message
            return thunkAPI.rejectWithValue(message)
        }
    }
)

export const createLionNote = createAsyncThunk(
    'meeting/createLionNote',
    async ({ meetingId, sectionKey, title, details }, thunkAPI) => {
        try {
            const userId = thunkAPI.getState().auth.user.uid
            const data = await meetingService.createLionNote({
                meetingId,
                sectionKey,
                title,
                details,
                userId,
            })
            return normalizeFirebaseData(data)
        } catch (error) {
            const message = error.message
            return thunkAPI.rejectWithValue(message)
        }
    }
)

export const meetingSlice = createSlice({
    name: 'meeting',
    initialState,
    reducers: {
        reset: () => initialState,
        setLionNotesBySection: (state, action) => {
            state.lionNotesBySection = action.payload
        },
        setMeeting: (state, action) => {
            state.meeting = action.payload
        },
        clearCalendarMeetings: (state) => {
            state.calendarMeetings = []
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createMeeting.pending, (state) => {
                state.isLoading = true
                state.loadingStates.createMeeting = true
                state.errors.createMeeting = null
            })
            .addCase(createMeeting.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.loadingStates.createMeeting = false
                state.errors.createMeeting = null
                state.meetings.push(action.payload)
            })
            .addCase(createMeeting.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
                state.loadingStates.createMeeting = false
                state.errors.createMeeting = action.payload
            })

            .addCase(getMeetings.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getMeetings = true
                state.errors.getMeetings = null
            })
            .addCase(getMeetings.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.meetings = normalizeFirebaseData(action.payload)
                state.isError = false
                state.loadingStates.getMeetings = false
                state.errors.getMeetings = null
                state.meetingsPagination = {
                    hasMore: true,
                    nextCursor: null,
                }
            })
            .addCase(getMeetings.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getMeetings = false
                state.errors.getMeetings = action.payload
            })

            .addCase(getMeetingsPage.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getMeetingsPage = true
                state.errors.getMeetingsPage = null
            })
            .addCase(getMeetingsPage.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                const payload = action.payload || {}
                const incoming = Array.isArray(payload.items) ? payload.items : []
                const shouldAppend = !!payload.append

                state.meetings = shouldAppend ? [...state.meetings, ...incoming] : incoming
                state.meetingsPagination = {
                    hasMore: !!payload.hasMore,
                    nextCursor: payload.nextCursor ?? null,
                }
                state.isError = false
                state.loadingStates.getMeetingsPage = false
                state.errors.getMeetingsPage = null
            })
            .addCase(getMeetingsPage.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getMeetingsPage = false
                state.errors.getMeetingsPage = action.payload
            })

            .addCase(getMeetingsByMonth.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getMeetingsByMonth = true
                state.errors.getMeetingsByMonth = null
            })
            .addCase(getMeetingsByMonth.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                const incoming = Array.isArray(action.payload) ? action.payload : []
                const map = new Map(
                    state.calendarMeetings.map((m) => [m.meetingId || m.id, m])
                )
                incoming.forEach((m) => {
                    map.set(m.meetingId || m.id, m)
                })
                state.calendarMeetings = Array.from(map.values())
                state.loadingStates.getMeetingsByMonth = false
                state.errors.getMeetingsByMonth = null
            })
            .addCase(getMeetingsByMonth.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getMeetingsByMonth = false
                state.errors.getMeetingsByMonth = action.payload
            })


            .addCase(getFutureMeetings.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getFutureMeetings = true
                state.errors.getFutureMeetings = null
            })
            .addCase(getFutureMeetings.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.meetings = normalizeFirebaseData(action.payload)
                state.isError = false
                state.loadingStates.getFutureMeetings = false
                state.errors.getFutureMeetings = null
            })
            .addCase(getFutureMeetings.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getFutureMeetings = false
                state.errors.getFutureMeetings = action.payload
            })

            .addCase(getPastMeetings.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getPastMeetings = true
                state.errors.getPastMeetings = null
            })
            .addCase(getPastMeetings.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.meetings = normalizeFirebaseData(action.payload)
                state.isError = false
                state.loadingStates.getPastMeetings = false
                state.errors.getPastMeetings = null
            })
            .addCase(getPastMeetings.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getPastMeetings = false
                state.errors.getPastMeetings = action.payload
            })

            .addCase(getSubMeetings.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getSubMeetings = true
                state.errors.getSubMeetings = null
            })
            .addCase(getSubMeetings.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.meetings = action.payload
                state.isError = false
                state.loadingStates.getSubMeetings = false
                state.errors.getSubMeetings = null
            })
            .addCase(getSubMeetings.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getSubMeetings = false
                state.errors.getSubMeetings = action.payload
            })

            .addCase(deleteMeeting.pending, (state) => {
                state.isLoading = true
                state.loadingStates.deleteMeeting = true
                state.errors.deleteMeeting = null
            })
            .addCase(deleteMeeting.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.meetings = state.meetings.filter((meeting) => meeting.id !== action.payload)
                state.isError = false
                state.loadingStates.deleteMeeting = false
                state.errors.deleteMeeting = null
            })
            .addCase(deleteMeeting.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.deleteMeeting = false
                state.errors.deleteMeeting = action.payload
            })

            .addCase(updateMeeting.pending, (state) => {
                state.isLoading = true
                state.loadingStates.updateMeeting = true
                state.errors.updateMeeting = null
            })
            .addCase(updateMeeting.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.loadingStates.updateMeeting = false
                state.errors.updateMeeting = null
                const index = state.meetings.findIndex(m => m.id === action.payload.id)
                if (index !== -1) {
                    state.meetings[index] = action.payload
                }
            })
            .addCase(updateMeeting.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.updateMeeting = false
                state.errors.updateMeeting = action.payload
            })

            .addCase(getMeeting.pending, (state) => {
                state.isLoading = true
                state.isSuccess = false
                state.loadingStates.getMeeting = true
                state.errors.getMeeting = null
            })
            .addCase(getMeeting.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.meeting = normalizeFirebaseData(action.payload)
                state.loadingStates.getMeeting = false
                state.errors.getMeeting = null
            })
            .addCase(getMeeting.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getMeeting = false
                state.errors.getMeeting = action.payload
            })

            .addCase(subscribeMeeting.pending, (state) => {
                state.loadingStates.subscribeMeeting = true
                state.errors.subscribeMeeting = null
            })
            .addCase(subscribeMeeting.fulfilled, (state) => {
                state.loadingStates.subscribeMeeting = false
                state.errors.subscribeMeeting = null
            })
            .addCase(subscribeMeeting.rejected, (state, action) => {
                state.loadingStates.subscribeMeeting = false
                state.errors.subscribeMeeting = action.payload
            })

            .addCase(getMeetingUsers.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getMeetingUsers = true
                state.errors.getMeetingUsers = null
            })
            .addCase(getMeetingUsers.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.meetingUsers = normalizeFirebaseData(action.payload)
                state.isError = false
                state.loadingStates.getMeetingUsers = false
                state.errors.getMeetingUsers = null
            })
            .addCase(getMeetingUsers.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getMeetingUsers = false
                state.errors.getMeetingUsers = action.payload
            })

            .addCase(getMeetingAgendas.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getMeetingAgendas = true
                state.errors.getMeetingAgendas = null
            })
            .addCase(getMeetingAgendas.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.meetingAgendas = normalizeFirebaseData(action.payload)
                state.isError = false
                state.loadingStates.getMeetingAgendas = false
                state.errors.getMeetingAgendas = null
            })
            .addCase(getMeetingAgendas.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
                state.loadingStates.getMeetingAgendas = false
                state.errors.getMeetingAgendas = action.payload
            })

            .addCase(getMeetingNotes.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getMeetingNotes = true
                state.errors.getMeetingNotes = null
            })
            .addCase(getMeetingNotes.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.meetingNotes = normalizeFirebaseData(action.payload)
                state.isError = false
                state.loadingStates.getMeetingNotes = false
                state.errors.getMeetingNotes = null
            })
            .addCase(getMeetingNotes.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getMeetingNotes = false
                state.errors.getMeetingNotes = action.payload
            })

            .addCase(createMeetingNote.pending, (state) => {
                state.isLoading = true
                state.loadingStates.createMeetingNote = true
                state.errors.createMeetingNote = null
            })
            .addCase(createMeetingNote.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.meetingNotes.unshift(action.payload)
                state.loadingStates.createMeetingNote = false
                state.errors.createMeetingNote = null
            })
            .addCase(createMeetingNote.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.createMeetingNote = false
                state.errors.createMeetingNote = action.payload
            })

            .addCase(joinMeeting.pending, (state) => {
                state.isLoading = true
                state.loadingStates.joinMeeting = true
                state.errors.joinMeeting = null
            })
            .addCase(joinMeeting.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.loadingStates.joinMeeting = false
                state.errors.joinMeeting = null
            })
            .addCase(joinMeeting.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.joinMeeting = false
                state.errors.joinMeeting = action.payload
            })

            .addCase(getMeetingsFront.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getMeetingsFront = true
                state.errors.getMeetingsFront = null
            })
            .addCase(getMeetingsFront.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.meetingsFront = action.payload
                state.isError = false
                state.loadingStates.getMeetingsFront = false
                state.errors.getMeetingsFront = null
            })
            .addCase(getMeetingsFront.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getMeetingsFront = false
                state.errors.getMeetingsFront = action.payload
            })

            .addCase(getMeetingsPast.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getMeetingsPast = true
                state.errors.getMeetingsPast = null
            })
            .addCase(getMeetingsPast.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.meetingsPast = action.payload
                state.isError = false
                state.loadingStates.getMeetingsPast = false
                state.errors.getMeetingsPast = null
            })
            .addCase(getMeetingsPast.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getMeetingsPast = false
                state.errors.getMeetingsPast = action.payload
            })

            .addCase(getMeetingAnnouncements.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getMeetingAnnouncements = true
                state.errors.getMeetingAnnouncements = null
            })
            .addCase(getMeetingAnnouncements.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.meetingAnnouncements = normalizeFirebaseData(action.payload)
                state.isError = false
                state.loadingStates.getMeetingAnnouncements = false
                state.errors.getMeetingAnnouncements = null
            })
            .addCase(getMeetingAnnouncements.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getMeetingAnnouncements = false
                state.errors.getMeetingAnnouncements = action.payload
            })

            .addCase(updateMeetingRecord.pending, (state) => {
                state.isLoading = true
                state.loadingStates.updateMeetingRecord = true
                state.errors.updateMeetingRecord = null
            })
            .addCase(updateMeetingRecord.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.meeting = action.payload
                state.loadingStates.updateMeetingRecord = false
                state.errors.updateMeetingRecord = null
            })
            .addCase(updateMeetingRecord.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
                state.loadingStates.updateMeetingRecord = false
                state.errors.updateMeetingRecord = action.payload
            })

            .addCase(getMeetingAIDetail.pending, (state) => {
                state.isLoading = true
                state.isSuccess = false
                state.loadingStates.getMeetingAIDetail = true
                state.errors.getMeetingAIDetail = null
            })
            .addCase(getMeetingAIDetail.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.meetingAIDetail = normalizeFirebaseData(action.payload)
                state.loadingStates.getMeetingAIDetail = false
                state.errors.getMeetingAIDetail = null
            })
            .addCase(getMeetingAIDetail.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getMeetingAIDetail = false
                state.errors.getMeetingAIDetail = action.payload
            })


            .addCase(getMeetingSpeakers.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getMeetingSpeakers = true
                state.errors.getMeetingSpeakers = null
            })
            .addCase(getMeetingSpeakers.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.meetingSpeakers = action.payload
                state.isError = false
                state.loadingStates.getMeetingSpeakers = false
                state.errors.getMeetingSpeakers = null
            })
            .addCase(getMeetingSpeakers.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getMeetingSpeakers = false
                state.errors.getMeetingSpeakers = action.payload
            })

            .addCase(getMeetingSpeechAnalysis.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getMeetingSpeechAnalysis = true
                state.errors.getMeetingSpeechAnalysis = null
            })
            .addCase(getMeetingSpeechAnalysis.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.meetingSpeechAnalysis = normalizeFirebaseData(action.payload)
                state.isError = false
                state.loadingStates.getMeetingSpeechAnalysis = false
                state.errors.getMeetingSpeechAnalysis = null
            })
            .addCase(getMeetingSpeechAnalysis.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.loadingStates.getMeetingSpeechAnalysis = false
                state.errors.getMeetingSpeechAnalysis = action.payload
            })


            .addCase(getMeetingDecisions.pending, (state) => {
                state.isLoading = true
                state.loadingStates.getMeetingDecisions = true
                state.errors.getMeetingDecisions = null
            })
            .addCase(getMeetingDecisions.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.meetingDecisions = normalizeFirebaseData(action.payload)
                state.isError = false
                state.loadingStates.getMeetingDecisions = false
                state.errors.getMeetingDecisions = null
            })
            .addCase(getMeetingDecisions.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
                state.loadingStates.getMeetingDecisions = false
                state.errors.getMeetingDecisions = action.payload
            })

            .addCase(subscribeLionNotes.pending, (state) => {
                state.loadingStates.subscribeLionNotes = true
                state.errors.subscribeLionNotes = null
            })
            .addCase(subscribeLionNotes.fulfilled, (state) => {
                state.loadingStates.subscribeLionNotes = false
                state.errors.subscribeLionNotes = null
            })
            .addCase(subscribeLionNotes.rejected, (state, action) => {
                state.loadingStates.subscribeLionNotes = false
                state.errors.subscribeLionNotes = action.payload
            })

            .addCase(createLionNote.pending, (state) => {
                state.isLoading = true
                state.loadingStates.createLionNote = true
                state.errors.createLionNote = null
            })
            .addCase(createLionNote.fulfilled, (state) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.loadingStates.createLionNote = false
                state.errors.createLionNote = null
            })
            .addCase(createLionNote.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
                state.loadingStates.createLionNote = false
                state.errors.createLionNote = action.payload
            })


    }
})

export const { reset, setLionNotesBySection, setMeeting, clearCalendarMeetings } = meetingSlice.actions
export default meetingSlice.reducer
